import { useMutation, useQueryClient, UseMutationOptions } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";

interface UsePrescriptionMutationOptions<TData, TVariables, TError = Error> {
  mutationFn: (variables: TVariables) => Promise<TData>;
  successMessage?: string | ((data: TData) => string);
  errorMessage?: string | ((error: TError) => string);
  invalidateQueries?: boolean;
  queryKey?: string[];
  onSuccess?: (data: TData, variables: TVariables, context: unknown) => void | Promise<void>;
  onError?: (error: TError, variables: TVariables, context: unknown) => void | Promise<void>;
  mutationOptions?: Omit<
    UseMutationOptions<TData, TError, TVariables, unknown>,
    "mutationFn" | "onSuccess" | "onError"
  >;
}

export function usePrescriptionMutation<TData, TVariables, TError = Error>({
  mutationFn,
  successMessage = "Operation completed successfully",
  errorMessage = "Operation failed",
  invalidateQueries = true,
  queryKey = ["prescriptions"],
  onSuccess: customOnSuccess,
  onError: customOnError,
  mutationOptions,
}: UsePrescriptionMutationOptions<TData, TVariables, TError>) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation<TData, TError, TVariables>({
    mutationFn,
    ...mutationOptions,
    onSuccess: (data, variables, context) => {
      // Invalidate queries if enabled
      if (invalidateQueries) {
        queryClient.invalidateQueries({ queryKey });
      }

      // Show success toast
      const message =
        typeof successMessage === "function"
          ? successMessage(data)
          : successMessage;
      toast({
        title: "Success",
        description: message,
      });

      // Call custom onSuccess if provided
      if (customOnSuccess) {
        customOnSuccess(data, variables, context);
      }
    },
    onError: (error, variables, context) => {
      // Show error toast
      const message =
        typeof errorMessage === "function"
          ? errorMessage(error)
          : (error as any)?.message || errorMessage || "Operation failed";
      toast({
        title: "Error",
        description: message,
        variant: "destructive",
      });

      // Call custom onError if provided
      if (customOnError) {
        customOnError(error, variables, context);
      }
    },
  });
}
